import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import toast, { Toaster } from 'react-hot-toast'
import { BrowserRouter, Route, Routes } from 'react-router'

import {
  BrowserCheck,
  SliderStackProvider,
  TooltipProvider,
} from '@fv/client-components'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  redirectToLogin,
} from '@fv/client-core'

import { AuthenticatedApp } from './AuthenticatedApp'
import { Login } from './features/auth/Login'
import { SessionGate } from './features/auth/SessionGate'
import { ErrorBoundary } from './features/layout/ErrorBoundary'
import { PickupNumberForm } from './features/public/PickupNumberForm'

const staleTime = 1000 * 60 * 2 // 2 minutes

function handleApiError(error: unknown) {
  const status = (error as { status?: number })?.status

  if (status === 401) {
    toast.error('Access denied, redirecting to login.', { id: 'unauthorized' })
    redirectToLogin()
  }

  if (status === 403) {
    toast.error(
      'You do not have access to the requested data. Please contact customer support.',
      { id: 'forbidden' },
    )
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  defaultOptions: {
    mutations: { onError: handleApiError },
    queries: {
      staleTime,
      retry: (failCount, error) => {
        const status = (error as { status?: number })?.status
        if (status === 401 || status === 403) return false
        return failCount < 3
      },
    },
  },
})

export const App = () => {
  return (
    <BrowserCheck>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delay={300} closeDelay={100}>
          <BrowserRouter>
            <ErrorBoundary>
              <SliderStackProvider>
                <Routes>
                  <Route path="login" element={<Login />}>
                    <Route path=":registrationToken" element={null} />
                  </Route>

                  <Route path="pickup/:loadId" element={<PickupNumberForm />} />

                  <Route
                    path="*"
                    element={
                      <SessionGate>
                        <AuthenticatedApp />
                      </SessionGate>
                    }
                  />
                </Routes>
              </SliderStackProvider>
            </ErrorBoundary>
          </BrowserRouter>

          <ReactQueryDevtools
            client={queryClient}
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        </TooltipProvider>
      </QueryClientProvider>

      <Toaster position="bottom-center" />
    </BrowserCheck>
  )
}
